import {
  Drawer,
  message,
  Select,
  Upload,
  Avatar,
  Form,
  Input,
  Popover,
} from "antd";
import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Icon } from "../../../components/Component";
import { Col, Spinner, Row } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import debounce from "lodash.debounce";
import { ApiResponse } from "../../../api/master/axiosIndex";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { projectsApis } from "../../../api/projects";
import { tasksApis } from "../../../api/NewTasks";
import { useLocation } from "react-router";
import ReactQuill from "react-quill";
import {
  UploadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import FormDatePicker from "../../form-components/DatePicker";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import ReminderPopper from "../NewTasks/ReminderPopper";
import { phaseApis } from "../../../api/Phase";
import { tagRender } from "../projects/projectCreation";
import { issueApis } from "../../../api/Issue";
import IssueApprovalDrawer from "./IssueApprovalDrawer";
import RecurringFrequencyPopper from "../NewTasks/RecurringFrequencyPopper";
import DocumentsTab from "../projectDocument/DocumentsTab";
import { useWbsName } from "../shared/useWbsCodes";

const Quill = ReactQuill.Quill;

const FontAttributor = Quill.import("formats/font") as any;
FontAttributor.whitelist = [
  "Roboto",
  "Arial",
  "Georgia",
  "Times-New-Roman",
  "Verdana",
  "Courier-New",
];
Quill.register(FontAttributor, true);

const SizeAttributor = Quill.import("formats/size") as any;
SizeAttributor.whitelist = [
  "6px",
  "8px",
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "32px",
  "48px",
];
Quill.register(SizeAttributor, true);

export interface IssueFormData {
  projectId: number;
  issueName: string;

  issueType?: string;
  description?: string;
  attachedFiles?: File[];

  assignee?: number; // single select
  dueDate?: string; // for UI

  severity?: number;
  priorityId?: number;
  releasePhase?: number[];
  linkedTask?: number;

  issueTags?: {
    tagName?: string;
    tagId?: number;
  }[];

  issueLinks?: {
    linkIssueId: number;
    linkType: string;
  }[];

  reminder?: string;
  reminders?: {
    reminderType?: string;
    specificDate?: string;
    specificTime?: string;
    daysOffset?: number;
    offsetFrom?: string;
    recurrenceType?: string;
    recurrenceInterval?: number;
    maxRecurrenceCount?: number;
    reminderUsers?: { userId: number }[];
    moduleType?: "Issues";
  };
  approvalRequired?: string;
  autoApprovalRemarks?: string;
  stateApprovalHierarchy?: any[];
  documentsRequired?: string;
  documentsData?: any[];
}

export interface IssueCreationPayload {
  projectId: number;
  activityId?: number;
  issueName: string;
  issueType: string;
  issueDescription: string;
  issueCriteria?: string;
  issueStartDate?: string;
  issueEndDate?: string;
  flag?: string;
  module?: string;
  priorityId?: number;
  affectedPhaseId?: number;
  releasePhaseId?: number;
  classification?: string;
  reproducible?: boolean;
  assignedTo?: number | null;
  taskId?: number | null;

  issueTags?: {
    tagName?: string;
    tagId?: number;
  }[];

  issueLinks?: {
    linkIssueId: number;
    linkType: string;
  }[];

  reminderFlag?: boolean;
  reminders?: {
    reminderType?: string;
    specificDate?: string;
    specificTime?: string;
    daysOffset?: number;
    offsetFrom?: string;
    recurrenceType?: string;
    recurrenceInterval?: number;
    maxRecurrenceCount?: number;
    reminderUsers?: { userId: number }[];
    moduleType?: "Issues";
  };
  approvalRequired?: boolean;
  autoApprovalRemarks?: string;
  stateApprovalHierarchy?: any[];
  docKeys?: any[];
}

interface ProjectUser {
  userId: string;
  userName: string;
  projectUserId: number;
  roleName: string;
}

interface Priority {
  priorityName: string;
  priorityId: number;
}

const IssuesCreation: FC<{
  projectId?: number;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  refreshData: () => void;
  taskDetails?: any;
  disableLinkField?: boolean;
}> = ({
  projectId: propProjectId,
  modalOpen,
  setModalOpen,
  refreshData,
  taskDetails,
  disableLinkField,
}) => {
  const location = useLocation();
  const wbsName = useWbsName();
  // Destructure routeProjectId and routeProjectName from location.state
  const { id: routeProjectId, projectName: routeProjectName } =
    location.state || {};
  // Prioritize propProjectId, then routeProjectId
  const currentProjectId = propProjectId || routeProjectId;

  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const siteId = currentUser?.siteAccessibility?.[0]?.siteID || null;

  const [messageApi, contextHolder] = message.useMessage();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [documents, setDocuments] = useState<{
    open: boolean;
    data: any[];
  }>({
    open: false,
    data: [],
  });
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<any[]>([]);

  const [taskDropdownData, setTaskDropdownData] = useState<SelectOption[]>([]);
  const [phaseDropdownData, setPhaseDropdownData] = useState<SelectOption[]>(
    []
  );

  const [projectTagsList, setProjectTagsList] = useState<any[]>([]);
  const [projectUsers, setProjectUsers] = useState<SelectOption[]>([]);
  // console.log(projectUsers,'projectusers');

  const [tags, setTags] = useState<any[]>([]);
  const [remainders, setRemainders] =
    useState<IssueCreationPayload["reminders"]>();
  const [priorityList, setPriorityList] = useState<SelectOption[]>([]);

  const methods = useForm<IssueFormData>({
    mode: "onChange",
    defaultValues: {
      // projectId: routeProjectName,
      projectId: currentProjectId,
      issueName: "",
      issueType: "",
      description: "",
      attachedFiles: [],
      reminder: "None",
      assignee: undefined,
      dueDate: undefined,
      severity: undefined,
      releasePhase: taskDetails?.phaseId ? [taskDetails.phaseId] : [],
      linkedTask: taskDetails?.taskId || undefined,
      issueTags: [],
      issueLinks: [],
      reminders: undefined,
      approvalRequired: "no",
      stateApprovalHierarchy: [],
      documentsRequired: "no",
      documentsData: [],
    },
  });

  const [taskPhases, setTaskPhases] = useState<SelectOption[]>([]);
  const [autoPopulatePhase, setAutoPopulatePhase] = useState(true);

  const [options, setOptions] = useState<
    { label: string; value: string | number }[]
  >([]);

  const { control, handleSubmit, setValue, watch, reset } = methods;

  const projectId = watch("projectId");
  const linkedTask = watch("linkedTask");
  const approvalRequired = watch("approvalRequired");
  const documentsRequired = watch("documentsRequired");
  const assignee = watch("assignee");

  // If the assignee is changed to someone already chosen as an approver,
  // drop them from the saved approval hierarchy — a user can't approve
  // their own issue. Handles the case where approvals were set before the
  // assignee was picked.
  useEffect(() => {
    if (assignee == null) return;
    setApprovals((prev) => {
      if (!prev.data?.length) return prev;
      const filtered = prev.data.filter(
        (row: any) => Number(row.userId) !== Number(assignee)
      );
      return filtered.length === prev.data.length
        ? prev
        : { ...prev, data: filtered };
    });
  }, [assignee]);

  // State for Issue Type Options
  const [issueTypeOptions, setIssueTypeOptions] = useState<SelectOption[]>([
    { label: "None", value: "None" }, // Initialize with "None"
  ]);
  const [isLoadingIssueTypes, setIsLoadingIssueTypes] = useState(false);

  const fetchIssueTypes = async (
    setIssueTypeOptions: React.Dispatch<
      React.SetStateAction<{ label: string; value: string }[]>
    >,
    setIsLoadingIssueTypes: React.Dispatch<React.SetStateAction<boolean>>,
    messageApi: any // or messageApi: MessageInstance if typed
  ) => {
    setIsLoadingIssueTypes(true);
    try {
      const response: ApiResponse = await issueApis.issueTypeList();

      if (response.status && Array.isArray(response.data)) {
        const fetchedOptions = response.data.map((item: string) => ({
          label: item,
          value: item,
        }));
        setIssueTypeOptions([
          { label: "None", value: "None" },
          ...fetchedOptions.filter((item) => item.value !== "None"),
        ]);
      } else {
        setIssueTypeOptions([{ label: "None", value: "None" }]);
        messageApi.error("Failed to load issue types or invalid data format.");
      }
    } catch (error) {
      console.error("Error fetching issue types:", error);
      setIssueTypeOptions([{ label: "None", value: "None" }]);
      messageApi.error("Failed to load issue types.");
    } finally {
      setIsLoadingIssueTypes(false);
    }
  };

  const fetchSeverity = async () => {
    try {
      const response = await tasksApis.priorityList({ listType: "Severity" });
      const { data, status }: { data: Priority[]; status: boolean } =
        response || {};
      if (status) {
        setPriorityList(
          data?.map((item) => ({
            label: item.priorityName,
            value: item.priorityId,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching priority list:", error);
      messageApi.error("Failed to fetch priority list");
    }
  };

  const fetchTagSuggestions = async (search: string) => {
    const dataToSearch = projectTagsList || [];
    const filtered =
      search && search.length >= 1
        ? dataToSearch.filter((tag) =>
            tag?.tagName?.toLowerCase()?.includes(search?.toLowerCase())
          )
        : dataToSearch;

    const formatted = filtered.map((tag) => ({
      label: tag?.tagName,
      value: tag?.tagId,
    }));

    if (formatted.length === 0 && search && search.length >= 1) {
      setOptions([{ label: search, value: "newTag__" + search }]);
    } else {
      setOptions(formatted);
    }
  };

  const debouncedFetch = debounce(fetchTagSuggestions, 100);

  const fetchProjectTags = async () => {
    try {
      const response: any = await projectsApis.fetchProjectTags(projectId);
      const {
        data: tagsList,
        status,
      }: {
        status: boolean;
        data: { tagName: string; tagId: number }[];
      } = response || {};
      if (status) {
        setProjectTagsList(tagsList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProjectUsers = async () => {
    try {
      const response = await projectsApis.fetchProjectUsers(projectId);
      const {
        data: usersList,
        status,
      }: { data: ProjectUser[]; status: boolean } = response || {};
      if (status) {
        const formatted = usersList?.map((user) => ({
          label: user.userName,
          value: Number(user.userId),
          roleName: user.roleName,
        }));
        setProjectUsers(formatted);
      }
    } catch (error) {
      console.error("Error fetching project users:", error);
      messageApi.error("Failed to fetch project users");
    }
  };

  // API call for tasks dropdown data
  const fetchTaskDropDownData = async (projectId: number) => {
    try {
      const apiResponse = await tasksApis.fetchTaskDropDown({ projectId });
      const { data, status } = apiResponse;
      if (status) {
        let options = data?.map((item: any) => {
          return {
            label: item.taskName,
            value: item.taskId,
            extra: {
              taskCompletionPercentage: item.taskCompletionPercentage,
            },
          };
        });

        // If taskDetails is provided, ensure the current task is in the dropdown with correct name
        if (taskDetails?.taskId && taskDetails?.taskName) {
          const taskId = taskDetails.taskId;
          const taskName = taskDetails.taskName;

          // Check if the task already exists in the dropdown
          const existingTaskIndex = options.findIndex(
            (opt: any) => opt.value === taskId
          );

          if (existingTaskIndex >= 0) {
            // Update the task name to match taskDetails
            options[existingTaskIndex].label = taskName;
          } else {
            // Add the task to the dropdown if it doesn't exist
            options.unshift({
              label: taskName,
              value: taskId,
            });
          }
        }

        setTaskDropdownData(options);
      } else {
        // Even if API fails, if taskDetails is provided, add it to dropdown
        if (taskDetails?.taskId && taskDetails?.taskName) {
          setTaskDropdownData([
            {
              label: taskDetails.taskName,
              value: taskDetails.taskId,
            },
          ]);
        } else {
          setTaskDropdownData([]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      // Even if API fails, if taskDetails is provided, add it to dropdown
      if (taskDetails?.taskId && taskDetails?.taskName) {
        setTaskDropdownData([
          {
            label: taskDetails.taskName,
            value: taskDetails.taskId,
          },
        ]);
      } else {
        setTaskDropdownData([]);
      }
    }
  };

  const fetchTaskByPhases = async (
    linkedTask: number | string | undefined,
    setTaskPhases: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    if (!linkedTask) {
      setTaskPhases([]);
      setValue("releasePhase", []);
      return;
    }

    try {
      const taskId =
        typeof linkedTask === "string" ? Number(linkedTask) : linkedTask;
      if (isNaN(taskId)) {
        setTaskPhases([]);
        setValue("releasePhase", []);
        return;
      }
      const response = await phaseApis.fetchTaskByPhaseDropDown(taskId);
      const { data, status } = response || {};

      if (status && data) {
        const formatted = Array.isArray(data)
          ? data.map((item) => ({ label: wbsName(item.phaseName), value: item.phaseId }))
          : [{ label: data.phaseName, value: data.phaseId }];
        setTaskPhases(formatted);
        // console.log("Fetched phases for task", linkedTask, ":", formatted);

        // AUTO-POPULATE logic:
        if (autoPopulatePhase && formatted.length === 1) {
          setValue("releasePhase", [formatted[0].value]);
        } else if (autoPopulatePhase) {
          // reset if multiple phases or none
          setValue("releasePhase", []);
        }
      } else {
        setTaskPhases([]);
        setValue("releasePhase", []);
      }
    } catch (error) {
      console.error("Failed to fetch task phases", error);
      setTaskPhases([]);
      setValue("releasePhase", []);
    }
  };

  const fetchPhaseDropDownData = async (projectId: number) => {
    try {
      const apiResponse = await phaseApis.fetchPhaseDropDown({ projectId });
      const { data, status } = apiResponse;
      if (status) {
        let options = data?.map((item: any) => {
          return {
            label: wbsName(item.phaseName),
            value: item.phaseId,
          };
        });
        setPhaseDropdownData(options);
      } else {
        setPhaseDropdownData([]);
      }
    } catch (error) {
      console.error("Failed to fetch phase:", error);
      setPhaseDropdownData([]);
    }
  };

  useEffect(() => {
    if (modalOpen) {
      fetchIssueTypes(setIssueTypeOptions, setIsLoadingIssueTypes, messageApi);
    }
  }, [modalOpen]);

  useEffect(() => {
    setAutoPopulatePhase(true);
    fetchTaskByPhases(linkedTask, setTaskPhases);
  }, [linkedTask]);

  useEffect(() => {
    if (currentProjectId && modalOpen) {
      fetchProjectUsers();
      fetchProjectTags();
      fetchSeverity();
    }
  }, [currentProjectId, modalOpen]);

  // Populate Tags dropdown options when projectTagsList loads
  useEffect(() => {
    if (projectTagsList?.length > 0) {
      fetchTagSuggestions("");
    }
  }, [projectTagsList]);

  const resetFormAndStates = () => {
    reset();
    setAttachedFiles([]);
    setTags([]);
    setRemainders(undefined);
    setApprovals({ open: false, data: [] });
    setDocuments({ open: false, data: [] });
    setDocumentFiles([]);
  };

  const submitNewIssue = async (data: IssueFormData) => {
    try {
      setDisableSubmit(true);

      // Check if approval is required but no approval data is set
      if (data.approvalRequired === "yes" && approvals.data.length === 0) {
        await Swal.fire({
          icon: "error",
          title: "Approval Required",
          text: 'Please set approval hierarchy by clicking "Set Approvals"',
          confirmButtonText: "OK",
        });
        setDisableSubmit(false);
        return;
      }

      // Check if documents is required but no documents data is set
      if (data.documentsRequired === "yes" && documents.data.length === 0) {
        await Swal.fire({
          icon: "error",
          title: "Documents Required",
          text: 'Please set documents by clicking "Set Documents"',
          confirmButtonText: "OK",
        });
        setDisableSubmit(false);
        return;
      }

      // Check if reminder is set but recurrence is not properly configured
      if (remainders) {
        // Check if we have reminder data but missing recurrence data
        const hasReminderData =
          remainders.reminderType && remainders.specificTime;
        const hasRecurrenceData =
          remainders.recurrenceType &&
          remainders.recurrenceType !== "None" &&
          remainders.recurrenceInterval &&
          remainders.maxRecurrenceCount;

        if (hasReminderData && !hasRecurrenceData) {
          messageApi.open({
            type: "error",
            content: "Recurrence is required when a reminder is selected",
          });
          setDisableSubmit(false);
          return;
        }

        // Additional validation for reminder fields
        if (!remainders.reminderType) {
          messageApi.open({
            type: "error",
            content: "Reminder type is required",
          });
          setDisableSubmit(false);
          return;
        }

        if (!remainders.specificTime) {
          messageApi.open({
            type: "error",
            content: "Reminder time is required",
          });
          setDisableSubmit(false);
          return;
        }

        // Additional validation for offset-based reminders
        if (
          ["Before", "After"].includes(remainders.reminderType) ||
          (remainders.reminderType === "On" &&
            remainders.offsetFrom !== "Specific Date")
        ) {
          if (!remainders.daysOffset || !remainders.offsetFrom) {
            messageApi.open({
              type: "error",
              content:
                "Offset-based reminders need 'daysOffset' and 'offsetFrom'",
            });
            setDisableSubmit(false);
            return;
          }
        }

        // For "On" reminder type, specificDate is only required if offsetFrom is "Specific Date"
        if (
          remainders.reminderType === "On" &&
          remainders.offsetFrom === "Specific Date" &&
          !remainders.specificDate
        ) {
          messageApi.open({
            type: "error",
            content:
              "Reminder type 'On' requires a specific date when 'Specific Date' is selected as offset",
          });
          setDisableSubmit(false);
          return;
        }
      }

      // Get activityId from taskDetails if available, or leave undefined
      const activityId = taskDetails?.activityId || undefined;

      // Get releasePhaseId - if array, take first value, otherwise undefined
      const releasePhaseId =
        Array.isArray(data.releasePhase) && data.releasePhase.length > 0
          ? data.releasePhase[0]
          : undefined;

      // Get affectedPhaseId - same as releasePhaseId if single value
      const affectedPhaseId = releasePhaseId || undefined;

      // Set issueStartDate to today if not provided, issueEndDate from dueDate
      const issueStartDate = dayjs().format("YYYY-MM-DD");
      const issueEndDate = data.dueDate
        ? dayjs(data.dueDate).format("YYYY-MM-DD")
        : undefined;

      const payload: IssueCreationPayload = {
        projectId: data.projectId,
        ...(activityId && { activityId }),
        issueName: data.issueName,
        issueType: data.issueType || "",
        issueDescription: data.description || "",
        issueCriteria: "", // Optional field, can be added later if needed
        issueStartDate: issueStartDate,
        ...(issueEndDate && { issueEndDate }),
        flag: "Internal", // Default to "Internal", can be made configurable later
        module: "", // Optional field, can be added later if needed
        priorityId: data.severity,
        ...(affectedPhaseId && { affectedPhaseId }),
        ...(releasePhaseId && { releasePhaseId }),
        classification: "", // Optional field, can be added later if needed
        reproducible: true, // Default to true, can be made configurable later
        assignedTo: data.assignee ?? null,
        taskId: data.linkedTask || null,

        issueTags:
          tags?.map((tag: any) => {
            if (typeof tag?.value === "string") {
              return {
                tagName: tag?.label,
              };
            }
            return {
              tagId: Number(tag?.value),
              tagName: tag?.label,
            };
          }) || [],

        issueLinks: data.issueLinks || [],

        reminderFlag: !!remainders,
        reminders: remainders
          ? {
              ...remainders,
              reminderUsers: remainders.reminderUsers || [],
              moduleType: "Issues",
            }
          : undefined,
        approvalRequired: data.approvalRequired === "yes",
        autoApprovalRemarks:
          data.approvalRequired === "no" ? data.autoApprovalRemarks : undefined,
        stateApprovalHierarchy: approvals.data,
        ...(data.documentsRequired === "yes" &&
          documents.data.length > 0 && {
            docKeys: documents.data,
          }),
      };

      // If documents with files are present, use FormData
      let response: ApiResponse;
      if (documentFiles.length > 0 && documents.data.length > 0) {
        const formDataToSend = new FormData();

        // Append files to FormData
        documentFiles.forEach((file) => {
          formDataToSend.append("files", file, file.name);
        });

        // Append the payload as JSON string with key "body"
        formDataToSend.append("body", JSON.stringify(payload));

        // Make API call with FormData
        response = await issueApis.createNewIssue(formDataToSend);
      } else {
        // Regular JSON payload without files
        // Still use FormData if there's a body field expected, otherwise send as JSON
        const formDataToSend = new FormData();
        formDataToSend.append("body", JSON.stringify(payload));

        response = await issueApis.createNewIssue(formDataToSend);
      }
      const {
        data: issue,
        status,
        error,
        message: apiMessage,
      } = response || {};

      if (status) {
        refreshData();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: issue?.message || apiMessage,
          confirmButtonText: "Ok",
          customClass: {
            container: "border-class",
          },
        }).then(() => {
          resetFormAndStates();
          setModalOpen(false);
        });
      } else {
        resetFormAndStates();
        messageApi.open({
          type: "error",
          content: error + ": " + apiMessage,
        });
      }
    } catch (error) {
      console.error("Error submitting new issue:", error);

      resetFormAndStates();
      messageApi.open({
        type: "error",
        content: "Failed to create issue. Please try again.",
      });
    } finally {
      setDisableSubmit(false);
    }
  };

  const handleFileUpload = (info: any) => {
    if (info.file.status !== "uploading") {
      const fileObj = info.file.originFileObj;
      setAttachedFiles((prev) => [...prev, fileObj]);
    }
  };

  const handleRemoveFile = (fileToRemove: any) => {
    setAttachedFiles((prev) => prev.filter((file) => file !== fileToRemove));
  };

  useEffect(() => {
    if (modalOpen && currentProjectId) {
      fetchTaskDropDownData(currentProjectId);
      fetchPhaseDropDownData(currentProjectId);
    } else {
      setTaskDropdownData([]);
    }
  }, [modalOpen, currentProjectId, taskDetails]);

  return (
    <>
      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>New Issue Creation for {routeProjectName} </strong>
            <>
              <div style={{ textAlign: "right" }}>
                {disableSubmit ? (
                  <Button color="primary" type="button">
                    <Spinner size="sm" color="light" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit(submitNewIssue)}
                    color="primary"
                    className="m-3 mt-0 mb-0 ps-4 pe-4"
                    size="sm"
                  >
                    <span>Save</span>
                  </Button>
                )}
              </div>
            </>
          </div>
        }
        placement="right"
        width={1200}
        onClose={() => {
          reset();
          setAttachedFiles([]);
          setTags([]);
          setDisableSubmit(false);
          setModalOpen(false);
          setTaskDropdownData([]);
          setDocuments({ open: false, data: [] });
          setApprovals({ open: false, data: [] });
        }}
        open={modalOpen}
        styles={{ body: { paddingBottom: 80 } }}
      >
        {contextHolder}
        {modalOpen && (
          <FormProvider {...methods}>
            <form
              className="content clearfix mt-2"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(submitNewIssue)();
              }}
            >
              <Row className="g-3">
                <Col md="6">
                  {/* Project Field - */}
                  {/* <FormInput
                  name="projectId"
                  label="Project"
                  required={true}
                  type="text"
                  placeholder="Project Name"
                  disabled={true}
                  value={routeProjectName || ""}
                /> */}
                  <div className="form-group">
                    <label className="form-label">Project</label>
                    <Input
                      value={routeProjectName || ""}
                      disabled
                      className="mt-1"
                    />
                    <FormInput
                      name="projectId"
                      label=""
                      type="hidden"
                      value={currentProjectId}
                    />
                  </div>
                </Col>
                {/* Issue Name Field */}
                <Col md="6">
                  <FormInput
                    name="issueName"
                    label="Issue Name"
                    required={true}
                    type="text"
                    placeholder=""
                  />
                </Col>

                <Col md="6">
                  <FormSelect
                    name="issueType"
                    label="Issue Type"
                    placeholder="Select Issue Type"
                    options={issueTypeOptions}
                    disabled={isLoadingIssueTypes}
                    // required
                  />
                </Col>

                <Col md="6">
                  <FormSelect
                    name="approvalRequired"
                    label="Work Approval Required ?"
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    placeholder="Select"
                    required={true}
                  />
                  <div style={{ display: "none" }}>
                    <FormInput
                      name="stateApprovalHierarchy"
                      label=""
                      type="text"
                    />
                  </div>
                  {watch("approvalRequired") == "yes" && (
                    <Popover
                      content="Click here to set required approvals"
                      title="Approval Required"
                    >
                      <a
                        href="#"
                        className="success"
                        onClick={(e) => {
                          e.preventDefault();
                          setApprovals((pre) => {
                            return {
                              ...pre,
                              open: true,
                            };
                          });
                        }}
                      >
                        <EditOutlined /> Set Approvals &nbsp;
                        {approvals?.data?.length == 0 ? (
                          <span className="mandatory">
                            Please set approvals
                          </span>
                        ) : (
                          <></>
                        )}
                      </a>
                    </Popover>
                  )}
                </Col>
                {watch("approvalRequired") == "no" && (
                  <Col md="12">
                    <FormInput
                      name="autoApprovalRemarks"
                      label="Remarks for No Approval"
                      type="textarea"
                      rows={2}
                      required={true}
                      minLength={2}
                      maxLength={500}
                      placeholder="Enter reason for not requiring approvals"
                    />
                  </Col>
                )}
                <Col md="6">
                  <FormSelect
                    name="documentsRequired"
                    label="Attach documents"
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    placeholder="Select"
                    required={true}
                  />
                  <div style={{ display: "none" }}>
                    <FormInput name="documentsData" label="" type="text" />
                  </div>
                  {watch("documentsRequired") == "yes" && (
                    <Popover
                      content={
                        documents?.data?.length > 0 ? (
                          <div style={{ maxWidth: 280 }}>
                            {documents.data.map((doc: any, idx: number) => (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  padding: "4px 0",
                                  wordBreak: "break-all",
                                }}
                              >
                                <Icon
                                  name="eye"
                                  style={{ color: "#4169E1" }}
                                />
                                <span>
                                  {documentFiles?.[idx]?.name ||
                                    doc?.customFileName ||
                                    `Document ${idx + 1}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          "Click here to set required documents"
                        )
                      }
                      title={
                        documents?.data?.length > 0
                          ? "Attached Documents"
                          : "Documents Required"
                      }
                    >
                      <a
                        href="#"
                        className="success"
                        onClick={(e) => {
                          e.preventDefault();
                          setDocuments((pre) => {
                            return {
                              ...pre,
                              open: true,
                            };
                          });
                        }}
                      >
                        <EditOutlined /> Set Documents &nbsp;
                        {documents?.data?.length == 0 ? (
                          <span className="mandatory">
                            Please set documents
                          </span>
                        ) : (
                          <span className="text-muted">
                            ({documents.data.length} attached)
                          </span>
                        )}
                      </a>
                    </Popover>
                  )}
                </Col>
              </Row>

              {/* Description Field */}
              <div className="row g-3 mt-1">
                <Col md="12">
                  <FormInput
                    name="description"
                    label="Description"
                    type="textarea"
                    rows={2}
                    placeholder="Enter description"
                  />
                </Col>
              </div>

              {/* Drop files or add attachments here... */}
              {/* <div className="row g-3 mt-3">
              <div className="col-12">
                <div
                  style={{
                    border: "1px dashed #d9d9d9",
                    borderRadius: "4px",
                    padding: "8px 12px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      Drop files or add Attachments Files
                    </div>
                  </div>

                  <Upload.Dragger
                    multiple
                    showUploadList={false}
                    customRequest={({ file, onSuccess }) => {
                      setTimeout(() => {
                        onSuccess?.("ok");
                        handleFileUpload({
                          file: { originFileObj: file, status: "done" },
                        });
                      }, 300);
                    }}
                    onRemove={handleRemoveFile}
                    style={{ background: "transparent", padding: "4px 0" }}
                  >
                    <div
                      style={{ textAlign: "left", fontSize: 12, color: "#555" }}
                    >
                      <UploadOutlined style={{ marginRight: 8 }} />
                      Drop files or click to upload
                    </div>
                  </Upload.Dragger>

                  {attachedFiles.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      {attachedFiles.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "4px 0",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <span>📎 {file.name}</span>
                          <Button
                            className="btn-icon"
                            size="sm"
                            onClick={() => handleRemoveFile(file)}
                            style={{
                              background: "transparent",
                              border: "none",
                              boxShadow: "none",
                              padding: 0,
                              fontSize: 12,
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div> */}

              <div className="row g-3 mt-1">
                <div className="col-4">
                  <label
                    className="form-label"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Reminder
                  </label>
                  <ReminderPopper
                    setRemainders={setRemainders}
                    users={projectUsers}
                  />
                </div>
                <div className="col-4">
                  <label
                    className="form-label"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Recurrence
                  </label>
                  <RecurringFrequencyPopper setRemainders={setRemainders} />
                </div>
              </div>

              {/* Issue Information Section Header */}
              <h6 className="mt-4 mb-3">Issue Information</h6>

              {/* Link Task Field (NEW) */}
              <div className="row g-3">
                <Col md="6">
                  <FormSelect
                    name="linkedTask"
                    label="Link Task"
                    // required={true}
                    placeholder="Select Task"
                    options={taskDropdownData?.map((task: any) => {
                      const pct = Number(
                        task?.extra?.taskCompletionPercentage ?? 0
                      );
                      return {
                        label: `${task.label} (${pct}%)`,
                        value: task.value,
                        disabled: pct === 0,
                      };
                    })}
                    disabled={disableLinkField}
                  />
                </Col>

                <Col md="6">
                  <FormSelect
                    name="releasePhase"
                    label="Release Phase"
                    placeholder="Select"
                    options={linkedTask ? taskPhases : phaseDropdownData}
                    disabled={disableLinkField || false}
                    onChange={(value) => {
                      if (value && value.length > 0) {
                        setAutoPopulatePhase(false); // disable auto populate if user manually picks phase
                      } else {
                        setAutoPopulatePhase(true); // if cleared, allow autoupdate again
                      }
                    }}
                  />
                </Col>
              </div>

              <div className="row g-3 mt-1">
                <Col md="6">
                  <FormSelect
                    name="assignee"
                    label="Assignee"
                    placeholder="Select Assignee"
                    options={projectUsers}
                    // showSearch
                    // optionFilterProp="label"
                  />
                </Col>
                {/* Tags Field (Moved to align) */}
                <Col lg="6">
                  <label className="form-label">Tags</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Type and press enter to create or select a tag"
                    labelInValue
                    value={tags}
                    onSearch={debouncedFetch}
                    onDropdownVisibleChange={(open) => {
                      if (open && projectTagsList?.length > 0) {
                        fetchTagSuggestions("");
                      }
                    }}
                    onChange={(value) => {
                      const latest: any = value?.slice(-1)[0]; // Get the last tag

                      if (latest?.label?.length < 2) {
                        // Check if the last tag length is less than 2 characters
                        messageApi.open({
                          type: "error",
                          content: "Enter at least 2 characters",
                          duration: 5,
                        });
                        setOptions([]);
                        return;
                      }
                      setTags(value);
                    }}
                    options={options}
                    tokenSeparators={[","]}
                    tagRender={tagRender}
                    filterOption={false} // Use custom filtering only
                  />
                </Col>
              </div>

              {/* Due Date, Severity Fields */}
              <div className="row g-3 mt-1">
                <Col md="6">
                  <FormDatePicker
                    name="dueDate"
                    label="Due Date"
                    placeholder="Select Date"
                    disabledDate={(current) =>
                      current && current < dayjs().startOf("day")
                    }
                  />
                </Col>
                <Col md="6">
                  <FormSelect
                    name="severity"
                    label="Severity"
                    placeholder="None"
                    options={priorityList}
                  />
                </Col>
              </div>
            </form>
          </FormProvider>
        )}
      </Drawer>
      {approvals.open && (
        <IssueApprovalDrawer
          open={approvals.open}
          projectId={currentProjectId || 0}
          excludeUserId={watch("assignee")}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any) => {
            console.log("data:111111", data);
            setApprovals({
              open: false,
              data: data,
            });
          }}
          setApprovalSubmissionStatus={(status: boolean) => {}}
          approvalData={approvals.data} // Pass the current approval data
        />
      )}
      {documents.open && (
        <DocumentsTab
          linkType="issue"
          linkToId={currentProjectId || 0}
          linkToName={watch("issueName") || ""}
          projectId={currentProjectId}
          projectName={routeProjectName}
          issueName={watch("issueName") || ""}
          shouldLoadData={false}
          isFromWorkUI={false}
          openDrawer={documents.open}
          onDrawerClose={() => {
            setDocuments((pre) => ({
              ...pre,
              open: false,
            }));
          }}
          onDocumentsSubmit={(data: { docKeys: any[]; files: File[] }) => {
            setDocuments({
              open: false,
              data: data.docKeys,
            });
            setDocumentFiles(data.files);
          }}
          initialDocData={
            documents.data.length > 0 || documentFiles.length > 0
              ? { docKeys: documents.data, files: documentFiles }
              : undefined
          }
        />
      )}
    </>
  );
};

export default IssuesCreation;
